import { FormHelperText } from "@mui/material";
import Checkbox, {
  CheckboxProps as MuiCheckboxProps,
} from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { CSSProperties } from "@mui/styles";
import { CHECKBOX_SIZE, CHECKBOX_SPACE } from "core/consts";
import { isPrintMode } from "core/model/utils/urls";
import { Validation, ValueOf } from "core/types";
import Tooltip from "ds_legacy/components/Tooltip";
import {
  ERROR_COLOR,
  TEXT_DARK_PRIMARY,
  WHITE,
} from "ds_legacy/materials/colors";
import { dp, padding, space } from "ds_legacy/materials/metrics";
import {
  FONT_SIZE_10,
  FONT_SIZE_13,
  FONT_SIZE_14,
  FONT_SIZE_15,
} from "ds_legacy/materials/typography";
import { ChangeEvent, InputHTMLAttributes, ReactNode } from "react";
import { FormElementRenderProps, isValid } from "react-forms-state";
import { useTranslations } from "translations";
import { v4 as uuidv4 } from "uuid";
import { getHelperText } from "../Validation";

export type CheckboxProps = {
  checkboxSize?: ValueOf<typeof CHECKBOX_SIZE>;
  checkboxSpace?: ValueOf<typeof CHECKBOX_SPACE>;
  checked?: MuiCheckboxProps["checked"];
  customValue?: string | null;
  shouldDisableChange?: boolean;
  disabled?: MuiCheckboxProps["disabled"];
  elementName?: string;
  hasFieldValidation?: boolean;
  withFlatModel?: boolean;
  hasBooleanValue?: boolean;
  isHidden?: boolean;
  id?: MuiCheckboxProps["id"];
  isImportant?: boolean;
  indeterminate?: MuiCheckboxProps["indeterminate"];
  inputProps?: MuiCheckboxProps["inputProps"];
  isGroup?: boolean;
  label: ReactNode | number | string;
  labelPlacement?: "bottom" | "end" | "start" | "top";
  labelStyleOverride?: CSSProperties;
  margin?: string;
  maxWidth?: string;
  onBeforeChange?: (value: any) => any;
  onChange?:
    | ((value: any, statePath?: string) => void)
    | ((value: any) => void);
  required?: MuiCheckboxProps["required"];
  sideMutation?: (
    value: boolean,
    mutateElement: (v: any, name: string) => void,
  ) => void;
  tooltip?: string;
  validation?: Validation;
  value?: MuiCheckboxProps["value"];
  width?: string;
};

export function isChecked(value: ToType, hasBooleanValue?: boolean) {
  if (hasBooleanValue) return Boolean(value);
  return value === "" ? true : Boolean(value);
}

function getFormControlLabelStyle({
  checkboxSpace,
  isGroup,
  isImportant,
  margin,
  maxWidth,
  validation,
  width,
}: Pick<
  CheckboxProps,
  | "checkboxSpace"
  | "isGroup"
  | "isImportant"
  | "margin"
  | "maxWidth"
  | "validation"
  | "width"
>): CSSProperties {
  let labelStyles: CSSProperties = {};

  if (isImportant) {
    labelStyles.fontSize = FONT_SIZE_15;
  } else {
    labelStyles.fontSize =
      checkboxSpace === CHECKBOX_SPACE.SMALL ? FONT_SIZE_13 : FONT_SIZE_14;
  }

  if (!isValid(validation)) {
    labelStyles = { ...labelStyles, color: isGroup ? undefined : ERROR_COLOR };
  } else {
    labelStyles = { ...labelStyles };
  }

  return {
    "@media print": {
      fontSize: FONT_SIZE_10,
    },
    "& .MuiFormControlLabel-label, & .MuiFormControlLabel-asterisk":
      labelStyles,
    "& .MuiFormControlLabel-label.Mui-disabled": {
      color: TEXT_DARK_PRIMARY,
      opacity: "1",
    },
    margin,
    maxWidth,
    width,
    wordBreak: "break-word",
  };
}

export function getCheckboxStyle({
  checkboxSize = CHECKBOX_SIZE.MEDIUM,
  checkboxSpace = CHECKBOX_SPACE.MEDIUM,
  isGroup,
  print,
  validation,
}: {
  checkboxSize?: ValueOf<typeof CHECKBOX_SIZE>;
  checkboxSpace?: ValueOf<typeof CHECKBOX_SPACE>;
  isGroup?: boolean;
  print: boolean;
  validation?: Validation;
}): CSSProperties {
  return {
    "& .MuiSvgIcon-root": {
      background: WHITE,
    },
    color: isGroup ? undefined : !isValid(validation) ? ERROR_COLOR : undefined,
    height: print ? dp(22) : space(checkboxSpace),
    padding: `${padding(0, 1.5)}!important`,
    transform: `scale(${checkboxSize})`,
  };
}

export function CheckboxInputFieldPresenter({
  checkboxSize = CHECKBOX_SIZE.MEDIUM,
  checkboxSpace = CHECKBOX_SPACE.MEDIUM,
  checked,
  customValue,
  disabled,
  elementName,
  hasBooleanValue,
  id,
  indeterminate,
  inputProps,
  isGroup,
  isHidden,
  isImportant = false,
  label,
  labelPlacement,
  labelStyleOverride,
  margin,
  maxWidth = "100%",
  onBeforeChange,
  onChange,
  required,
  shouldDisableChange,
  tooltip,
  validation,
  value,
  width,
}: CheckboxProps) {
  const translations = useTranslations();
  const isPrint = isPrintMode();
  const canUseCustomValue = typeof customValue !== "undefined";
  const errorTextId = `${elementName}-error-${uuidv4()}`;
  const hasError = !isValid(validation);
  if (isHidden) return null;

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = e.target;

    if (shouldDisableChange) return;

    if (canUseCustomValue) {
      onChange?.(checked ? value : null, elementName);
      return;
    }

    if (onChange && onBeforeChange) {
      onChange(onBeforeChange(checked));
    } else if (onChange) {
      onChange(checked);
    }
  };

  return (
    <>
      <Tooltip title={tooltip}>
        <FormControlLabel
          control={
            <Checkbox
              checked={
                canUseCustomValue ? checked : isChecked(value, hasBooleanValue)
              }
              color="primary"
              disabled={disabled}
              id={id}
              indeterminate={indeterminate}
              inputProps={{
                ...inputProps,
                ...({
                  "aria-describedby": `${hasError ? errorTextId : ""} ${
                    inputProps?.["aria-describedby"] ?? ""
                  }`.trim(),
                  "aria-invalid": hasError ? true : undefined,
                  "data-testid": `checkbox_${elementName}`,
                } as InputHTMLAttributes<HTMLInputElement>),
              }}
              name={elementName}
              sx={getCheckboxStyle({
                checkboxSize,
                checkboxSpace,
                isGroup,
                print: isPrint,
                validation,
              })}
              value={canUseCustomValue ? customValue : String(elementName)}
              onChange={onChangeHandler}
            />
          }
          label={label}
          labelPlacement={labelPlacement}
          name={String(elementName)}
          required={required}
          sx={{
            ...getFormControlLabelStyle({
              checkboxSpace,
              isGroup,
              isImportant,
              margin,
              maxWidth,
              validation,
              width,
            }),
            ...labelStyleOverride,
          }}
        />
      </Tooltip>
      {!isGroup && hasError && (
        <FormHelperText error={hasError} id={errorTextId}>
          {getHelperText({
            hasCustomValidation: true,
            translations,
            validation,
          })}
        </FormHelperText>
      )}
    </>
  );
}

const CheckboxInputField = ({
  elementName,
  hasFieldValidation,
  sideMutation,
  withFlatModel,
  ...props
}: Omit<CheckboxProps, "checked">) => (
  <FormElementRenderProps
    elementName={elementName}
    sideMutation={sideMutation}
    withFlatModel={withFlatModel}
  >
    {({ onChange, validation, value }) => {
      const canUseCustomValue = typeof props.customValue !== "undefined";
      return (
        <CheckboxInputFieldPresenter
          checked={canUseCustomValue ? !!value : undefined}
          elementName={elementName}
          validation={hasFieldValidation ?? validation}
          value={value}
          onChange={onChange}
          {...props}
        />
      );
    }}
  </FormElementRenderProps>
);

export default CheckboxInputField;
